import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { eq, not, or } from 'tio-ui/utilities';

import { registerDestructor } from '@ember/destroyable';
import fileQueue from 'ember-file-upload/helpers/file-queue';
import FileDropzone from 'ember-file-upload/components/file-dropzone';
import Text from 'tio-reimagined/components/text';
import type { UploadFile, Queue } from 'ember-file-upload';
import type ObservablesService from 'tio-common/services/observables';
import type { ObservableDocumentAttributes } from 'tio-common/services/observables';
import type CableService from 'tio-common/services/cable';
import type ObservableDocumentModel from 'tio-data/models/observable-document';
import type { IntlService } from 'ember-intl';
import type PersonModel from 'tio-data/models/person';
import type Owner from '@ember/owner';
import {
  PhWarningCircle,
  PhCaretDown,
  PhCaretUp,
  PhNumberCircleOne,
  PhNumberCircleTwo,
  PhNumberCircleThree,
  PhCloudArrowUp,
  PhFile,
  PhXCircle,
  PhCheckCircle,
  PhWarning,
} from 'ember-phosphor-icons';

const ACCEPTED_FILE_TYPES = '.pdf';
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const VALID_EXTENSIONS = ['pdf'];
const PROCESSING_TIMEOUT_MS = 60_000;

type UploadState = 'idle' | 'file-selected' | 'uploading' | 'processing' | 'success' | 'error';

type BatchStatusAttributes = Partial<ObservableDocumentAttributes> & {
  extractionState?: ObservableDocumentModel['extractionState'];
  reflectionState?: ObservableDocumentModel['reflectionState'];
};

type SlrAlert = Pick<import('tio-employee/services/reimagined-data').SlrAlert, 'description'>;

type BatchStatus = {
  id: string;
  recordAttributes: BatchStatusAttributes;
  transition: { event: string };
  slrAlerts?: SlrAlert[];
};

interface IssueNotificationCardSignature {
  Args: {
    /** Header title for the issue */
    title: string;
    /** Description of the action required */
    description: string;
    /** Person whose documents are being uploaded */
    person: PersonModel;
    /** Whether the card starts expanded */
    isExpanded?: boolean;
    /** Custom success message shown after upload completes (overrides default) */
    successMessage?: string;
  };
  Element: HTMLDivElement;
}

const getFileExtension = (filename: string) => {
  return filename.split('.').pop()?.toLowerCase() ?? '';
};

export default class IssueNotificationCard extends Component<IssueNotificationCardSignature> {
  @service declare observables: ObservablesService;
  @service declare cable: CableService;
  @service declare intl: IntlService;
  @service declare fileQueue: Queue;

  @tracked isExpanded = this.args.isExpanded ?? false;
  @tracked state: UploadState = 'idle';
  @tracked upload?: UploadFile;
  @tracked fileValidationError: string | null = null;
  @tracked uploadError: string | null = null;
  @tracked processingTimedOut = false;

  batchId = crypto.randomUUID();
  private subscriptionParams: object | null = null;
  private processingTimer?: ReturnType<typeof setTimeout>;

  constructor(owner: Owner, args: IssueNotificationCardSignature['Args']) {
    super(owner, args);
    registerDestructor(this, () => {
      this.unsubscribe();
      clearTimeout(this.processingTimer);
    });
  }

  get queue() {
    // @ts-expect-error findOrCreate exists on Queue
    return this.fileQueue.findOrCreate(`issue-notification-upload-${this.batchId}`);
  }

  get queueName() {
    return `issue-notification-upload-${this.batchId}`;
  }

  get hasFile() {
    return !!this.upload;
  }

  toggleExpanded = () => {
    this.isExpanded = !this.isExpanded;
  };

  private unsubscribe = () => {
    if (this.subscriptionParams) {
      this.cable.unsubscribe(this.subscriptionParams);
      this.subscriptionParams = null;
    }
  };

  private subscribeToBatchStatus = () => {
    const channelParams = {
      channel: 'ObservableDocumentBatchStatusChannel',
      batch: this.batchId,
    };

    this.subscriptionParams = channelParams;

    this.cable.subscribe(channelParams, {
      received: (data: unknown) => {
        const msg = data as BatchStatus;
        const { id, recordAttributes, transition } = msg;

        this.observables.updateObservableDocument({ ...recordAttributes, id });

        const extractionFailed = ['ExtractionState.FAILED', 'ExtractionState.INVALID'].includes(
          recordAttributes.extractionState ?? ''
        );

        if (extractionFailed) {
          this.unsubscribe();
          clearTimeout(this.processingTimer);
          const isInvalidDocument = recordAttributes.extractionState === 'ExtractionState.INVALID';
          this.uploadError = isInvalidDocument
            ? this.intl.t('observability.wizard.upload.error_invalid_document')
            : this.intl.t('observability.wizard.upload.error_failed');
          this.state = 'error';
          return;
        }

        const reflectionSuccessful = transition.event === 'content_reflected';
        const reflectionFailed = transition.event === 'reflection_failed';

        if (reflectionSuccessful || reflectionFailed) {
          this.unsubscribe();
          clearTimeout(this.processingTimer);

          if (reflectionSuccessful) {
            // Check post-reflection SLR alerts from payment validation
            const [firstAlert] = msg.slrAlerts ?? [];
            if (firstAlert) {
              this.uploadError =
                firstAlert.description ?? this.intl.t('observability.wizard.upload.error_failed');
              this.state = 'error';
            } else {
              this.state = 'success';
            }
          } else {
            this.uploadError = this.intl.t('observability.wizard.upload.error_failed');
            this.state = 'error';
          }
        }
      },
    });

    this.processingTimer = setTimeout(() => {
      if (this.state === 'processing') {
        this.processingTimedOut = true;
      }
    }, PROCESSING_TIMEOUT_MS);
  };

  handleFileAdded = (uploadFile: UploadFile) => {
    this.fileValidationError = null;
    this.uploadError = null;
    const file = uploadFile.file;

    const ext = getFileExtension(file.name);
    if (!VALID_EXTENSIONS.includes(ext)) {
      this.fileValidationError = this.intl.t('observability.wizard.upload.invalid_file_type');
      this.queue.remove(uploadFile);
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      this.fileValidationError = this.intl.t('observability.wizard.upload.file_too_large', {
        fileSize: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
        maxSize: MAX_FILE_SIZE_MB,
      });
      this.queue.remove(uploadFile);
      return;
    }

    this.upload = uploadFile;
    this.state = 'file-selected';
  };

  removeFile = () => {
    this.queue.remove(this.upload);
    this.upload = undefined;
    this.fileValidationError = null;
    this.state = 'idle';
  };

  handleSubmit = async (e: Event) => {
    e.preventDefault();
    if (!this.upload) return;

    this.unsubscribe();
    clearTimeout(this.processingTimer);
    this.processingTimedOut = false;
    this.state = 'uploading';
    this.subscribeToBatchStatus();

    try {
      await this.observables.createObservableDocument(this.upload, {
        person: this.args.person,
        batch: this.batchId,
      });

      this.state = 'processing';
    } catch {
      this.uploadError = this.intl.t('observability.wizard.upload.error_failed');
      this.state = 'error';
      this.unsubscribe();
      clearTimeout(this.processingTimer);
      this.processingTimedOut = false;
    }
  };

  handleDoneClick = (e: Event) => {
    e.preventDefault();
    if (this.state === 'success') {
      this.isExpanded = false;
      return;
    }
    if (this.upload) {
      void this.handleSubmit(e);
    }
  };

  reset = () => {
    this.unsubscribe();
    clearTimeout(this.processingTimer);
    this.queue.remove(this.upload);
    this.upload = undefined;
    this.fileValidationError = null;
    this.uploadError = null;
    this.processingTimedOut = false;
    this.state = 'idle';
    this.batchId = crypto.randomUUID();
  };

  <template>
    {{! Figma: Notification/No Longer Eligible — 4 states:
        collapsed, expanded (no file), expanded (file uploaded), success/processing }}
    <div class="tio-issue-card" data-test-issue-notification-card ...attributes>
      {{#if (eq this.state "success")}}
        {{#if @successMessage}}
          {{! ── Success State (custom): clean card with checkmark (Figma: Notification/PIF Accounts/Success) ── }}
          <div class="tio-issue-card__success-row" data-test-issue-notification-success>
            <div class="tio-issue-card__success-badge tio-bg-success-soft">
              <PhCheckCircle @size={{20}} class="tio-fg-success" @weight="fill" />
            </div>
            <Text @variant="label">
              {{@successMessage}}
            </Text>
          </div>
        {{else}}
          {{! ── Success State (default): full success with upload-another option ── }}
          <div class="tio-issue-card__success-col" data-test-issue-notification-success>
            <div class="tio-issue-card__success-badge-lg tio-bg-success-soft">
              <PhCheckCircle @size={{24}} class="tio-fg-success" />
            </div>
            <Text @variant="success" @size="lg" @weight="semibold">
              {{t "observability.wizard.complete.heading"}}
            </Text>
            <Text @variant="success" @size="sm">
              {{t
                "observability.wizard.complete.description"
                docType=(t "observability.wizard.doc_type.statement")
              }}
            </Text>
            <button
              type="button"
              class="tio-issue-card__upload-another tio-fg-success-body"
              {{on "click" this.reset}}
            >
              {{t "observability.wizard.complete.upload_another"}}
            </button>
          </div>
        {{/if}}
      {{else}}
        {{! ── Top Row: Icon + Title/Description + Caret Toggle ── }}
        <button
          type="button"
          class="tio-issue-card__toggle"
          data-test-issue-notification-toggle
          aria-expanded="{{this.isExpanded}}"
          aria-label={{if
            this.isExpanded
            (t "slr.reimagined.collapse_action_item")
            (t "slr.reimagined.expand_action_item")
          }}
          {{on "click" this.toggleExpanded}}
        >
          <div class="tio-issue-card__toggle-main">
            <div class="tio-issue-card__icon-box tio-bg-danger-soft">
              <PhWarningCircle @size={{32}} class="tio-fg-danger" />
            </div>
            <div class="tio-issue-card__titles">
              <Text
                @variant="label"
                @weight="semibold"
                class="capitalize tracking-tight leading-snug"
              >
                {{@title}}
              </Text>
              <Text @variant="muted" @weight="medium" class="leading-snug">
                {{@description}}
              </Text>
            </div>
          </div>
          <div class="tio-issue-card__caret-box">
            {{#if this.isExpanded}}
              <PhCaretUp @size={{24}} class="tio-issue-card__caret" />
            {{else}}
              <PhCaretDown @size={{24}} class="tio-issue-card__caret" />
            {{/if}}
          </div>
        </button>

        {{! ── Expanded Content Area ── }}
        {{#if this.isExpanded}}
          <div
            class="tio-bg-info-soft tio-stack-start p-4 rounded-2xl w-full"
            data-test-issue-notification-expanded
          >
            {{#if (eq this.state "error")}}
              {{! ── Error State: replaces form content (Figma: Notification/*/Failure) ── }}
              <div class="tio-stack-start gap-4 w-full py-4 px-2">
                <Text
                  class="tio-fg-secondary leading-relaxed"
                  data-test-issue-notification-upload-error
                >
                  {{this.uploadError}}
                </Text>
                <button
                  type="button"
                  class="tio-row-center px-6 py-2 rounded-md font-semibold text-sm tio-fg-inverse bg-teal-600 cursor-pointer hover:bg-teal-700 leading-none"
                  data-test-issue-notification-try-again
                  {{on "click" this.reset}}
                >
                  {{t "observability.wizard.upload.try_again"}}
                </button>
              </div>
            {{else if (or (eq this.state "uploading") (eq this.state "processing"))}}
              {{! ── Processing State: replaces form content (Figma: Notification/*/Processing) ── }}
              <div class="tio-row-tight w-full py-2" data-test-issue-notification-progress-bar>
                <Text @size="sm" @weight="medium" class="tio-fg-secondary whitespace-nowrap">
                  {{t "slr.reimagined.processing_message"}}
                </Text>
                <div class="flex-1 rounded-full processing-indicator"></div>
              </div>
              {{#if this.processingTimedOut}}
                <div
                  class="tio-bg-warning-soft border tio-border-warning rounded-lg p-3 tio-row-top-tight w-full mt-2"
                >
                  <PhWarning @size={{16}} class="tio-fg-warning shrink-0 mt-0.5" />
                  <Text @variant="warning" @size="xs">
                    {{t "observability.wizard.upload.processing_slow"}}
                  </Text>
                </div>
              {{/if}}
            {{else}}
              {{! ── Default: Instructions + Upload ── }}
              <div class="tio-stack-start lg:flex-row w-full gap-4">
                {{! Left Column: Instructions }}
                <div
                  class="tio-stack-start gap-8 flex-1 min-w-0 px-2 sm:px-4 lg:pl-11 lg:pr-6 py-4"
                >
                  {{! Intro text }}
                  <Text @weight="medium" class="tio-fg-secondary leading-relaxed">
                    {{t "slr.reimagined.issue_notification.intro"}}
                  </Text>

                  {{! Steps section }}
                  <div class="tio-stack-start gap-4 w-full">
                    <Text @weight="semibold" class="leading-none">
                      {{t "slr.reimagined.issue_notification.steps_heading"}}
                    </Text>

                    <div class="tio-stack-start gap-4 pl-2 w-full">
                      {{! Step 1 }}
                      <div class="flex gap-1.5 items-start w-full">
                        <PhNumberCircleOne
                          @size={{26}}
                          class="tio-fg-danger shrink-0"
                          @weight="fill"
                        />
                        <Text @weight="medium" class="tio-fg-secondary leading-relaxed flex-1">
                          <span class="font-semibold tio-fg-primary">{{t
                              "slr.reimagined.issue_notification.step1_label"
                            }}</span>
                          {{t "slr.reimagined.issue_notification.step1_text"}}
                        </Text>
                      </div>

                      {{! Step 2 }}
                      <div class="flex gap-1.5 items-start w-full">
                        <PhNumberCircleTwo
                          @size={{26}}
                          class="tio-fg-danger shrink-0"
                          @weight="fill"
                        />
                        <Text @weight="medium" class="tio-fg-secondary leading-relaxed flex-1">
                          <span class="font-semibold tio-fg-primary">{{t
                              "slr.reimagined.issue_notification.step2_label"
                            }}</span>
                          {{t "slr.reimagined.issue_notification.step2_text"}}
                        </Text>
                      </div>

                      {{! Step 3 }}
                      <div class="flex gap-1.5 items-start w-full">
                        <PhNumberCircleThree
                          @size={{26}}
                          class="tio-fg-danger shrink-0"
                          @weight="fill"
                        />
                        <Text @weight="medium" class="tio-fg-secondary leading-relaxed flex-1">
                          <span class="font-semibold tio-fg-primary">{{t
                              "slr.reimagined.issue_notification.step3_label"
                            }}</span>
                          {{t "slr.reimagined.issue_notification.step3_text"}}
                        </Text>
                      </div>
                    </div>
                  </div>

                  {{! Can't find it? }}
                  <Text @weight="medium" class="tio-fg-secondary leading-relaxed">
                    <span class="font-semibold tio-fg-primary">{{t
                        "slr.reimagined.issue_notification.cant_find_label"
                      }}</span>
                    {{t "slr.reimagined.issue_notification.cant_find_text"}}
                  </Text>
                </div>

                {{! Right Column: Upload + Submit Button }}
                <div
                  class="flex flex-col justify-between px-2 sm:px-4 lg:pl-6 lg:pr-11 py-4 w-full lg:w-[45%] self-stretch"
                >
                  <div class="tio-stack-start flex-1 gap-3 w-full">
                    {{! Upload Dropzone — custom styled to match Figma }}
                    {{#unless this.upload}}
                      {{#let
                        (fileQueue name=this.queueName onFileAdded=this.handleFileAdded)
                        as |queue|
                      }}
                        <FileDropzone @queue={{queue}} class="w-full" as |dropzone|>
                          <label
                            data-test-issue-notification-dropzone
                            class="tio-place-center gap-4 w-full min-h-[200px] lg:min-h-[250px] bg-gray-200 border border-dashed tio-border-strong rounded-xl cursor-pointer p-4"
                          >
                            {{#if dropzone.active}}
                              <Text @size="sm" class="tio-fg-secondary">
                                {{t "slr.reimagined.drop_to_upload"}}
                              </Text>
                            {{else}}
                              <div class="tio-stack-center gap-2">
                                <PhCloudArrowUp @size={{40}} class="tio-fg-muted" />
                                <Text @weight="medium" class="text-center">
                                  <span class="font-semibold tio-fg-primary underline">{{t
                                      "slr.reimagined.select_a_file"
                                    }}</span>
                                  <span class="tio-fg-primary">
                                    {{t "slr.reimagined.drop_to_upload"}}
                                  </span>
                                </Text>
                              </div>
                              <Text
                                @size="xs"
                                @weight="medium"
                                class="tio-fg-secondary text-center"
                              >
                                {{t "slr.reimagined.issue_notification.supported_format"}}
                              </Text>
                            {{/if}}
                            <input
                              {{queue.selectFile}}
                              type="file"
                              class="sr-only"
                              accept={{ACCEPTED_FILE_TYPES}}
                            />
                          </label>
                        </FileDropzone>
                      {{/let}}
                    {{/unless}}

                    {{! File Tag (when file selected) }}
                    {{#if this.upload}}
                      <div class="flex items-start" data-test-issue-notification-file-tag>
                        <div class="bg-gray-200 tio-row-tight px-1.5 py-0.5 rounded-lg">
                          <div class="flex items-center gap-0.5">
                            <PhFile @size={{14}} class="tio-fg-primary shrink-0" />
                            <Text
                              @size="xs"
                              @weight="medium"
                              class="leading-4 truncate max-w-[200px]"
                            >
                              {{this.upload.file.name}}
                            </Text>
                          </div>
                          <button
                            type="button"
                            class="cursor-pointer shrink-0"
                            data-test-issue-notification-remove-file
                            aria-label={{t "slr.reimagined.issue_notification.remove_file"}}
                            {{on "click" this.removeFile}}
                          >
                            <PhXCircle @size={{14}} class="tio-fg-muted" />
                          </button>
                        </div>
                      </div>
                    {{/if}}

                    {{! Validation/upload errors }}
                    {{#if this.fileValidationError}}
                      <div
                        class="rounded-xl border tio-border-danger tio-bg-danger-soft p-3 tio-row-top-tight w-full"
                        data-test-issue-notification-validation-error
                      >
                        <PhWarning @size={{16}} class="tio-fg-danger shrink-0 mt-0.5" />
                        <Text @size="xs" class="tio-fg-danger-h">{{this.fileValidationError}}</Text>
                      </div>
                    {{/if}}
                  </div>

                  {{! Submit Button }}
                  <button
                    type="button"
                    data-test-issue-notification-done
                    class="tio-row-center w-full min-w-[168px] px-3 py-2 rounded-md font-semibold text-sm text-white mt-4 leading-none
                      {{if
                        this.hasFile
                        'bg-teal-600 cursor-pointer hover:bg-teal-700'
                        'bg-gray-400 opacity-40 cursor-not-allowed'
                      }}"
                    disabled={{not this.hasFile}}
                    {{on "click" this.handleDoneClick}}
                  >
                    {{t "slr.reimagined.submit"}}
                  </button>
                </div>
              </div>
            {{/if}}
          </div>
        {{/if}}
      {{/if}}
    </div>
  </template>
}
